/**
 * WhatsApp Queue Manager
 * Manage queues with user assignments for incoming WhatsApp messages
 */

import React, { useState, useEffect, useCallback } from 'react';
import config from '../../config/config';

const API_BASE = config.api.baseUrl;

const WhatsAppQueueManager = ({ currentUser }) => {
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    profiles: [], // List of profile names that can access this queue
    members: [], // List of user emails
    isActive: true,
    priority: 1,
    workingHours: {
      enabled: false,
      start: '08:30',
      end: '17:00',
      days: [0, 1, 2, 3, 4] // Sunday to Thursday
    },
    autoAssign: true, // Auto-assign to available agent
    routingType: 'round_robin' // round_robin, least_busy, manual
  });

  // 2026-05-31 owner-spec (Goal 3b): members were previously hard-filtered
  // by `form.profiles`. Two real problems with that:
  //   1. Owner needs to route a single queue to a small, hand-picked set of
  //      users that doesn't line up with any profile (e.g. "2 specific
  //      sales managers out of 6"). The old UI made that impossible —
  //      you had to either add the user to the profile, or change the
  //      queue's profile mix to reach them.
  //   2. If a saved member's profile changed later, they vanished from the
  //      editor (still saved on the queue, but invisible) — silent data loss.
  // Now: search box + "הצג את כל המשתמשים" toggle. Profile-filtered view
  // remains the default ergonomic. Selected members are ALWAYS visible,
  // regardless of the current filter, so we can never lose track of them.
  const [memberSearch, setMemberSearch] = useState('');
  const [showAllUsers, setShowAllUsers] = useState(false);

  // 2026-05-30 owner-spec (Goal 3a): profile list was previously a 14-row
  // hardcoded array that fell behind every time a profile was added in
  // Setup. Now loaded live from /api/setup/profiles (the canonical
  // profiles table — see backend/database/migrations/2026-05-29_canonical_profiles_table.sql)
  // so a queue editor opened today sees every active profile, including
  // the ones added since the original hardcode (e.g. הנהלת מוקד מכירה,
  // הנהלת מחסן, הנהלת משווקים, מוקד גביה, מוקד יזום, מזכירות, etc.).
  // Falls back to the legacy hardcoded set if the BE call fails so the
  // editor never renders an empty profile picker on a transient blip.
  const [profileList, setProfileList] = useState([
    // Minimal safety fallback — only used if the API call fails.
    { id: 'Admin', label: 'מנהל מערכת' },
    { id: 'sales_center', label: 'מוקד מכירה' },
    { id: 'service_center', label: 'מוקד שירות' },
  ]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/setup/profiles`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d?.profiles?.length) return;
        setProfileList(
          d.profiles.map(p => ({ id: p.id, label: p.label || p.id }))
        );
      })
      .catch(() => { /* keep the fallback */ });
    return () => { cancelled = true; };
  }, []);

  const ROUTING_TYPES = [
    { id: 'round_robin', label: 'סבב (Round Robin)', desc: 'חלוקה שווה בין הנציגים' },
    { id: 'least_busy', label: 'הפחות עמוס', desc: 'הפנייה לנציג עם הכי פחות שיחות פתוחות' },
    { id: 'manual', label: 'ידני', desc: 'הודעות נכנסות לתור להקצאה ידנית' }
  ];

  const DAYS = [
    { id: 0, label: 'א\'' },
    { id: 1, label: 'ב\'' },
    { id: 2, label: 'ג\'' },
    { id: 3, label: 'ד\'' },
    { id: 4, label: 'ה\'' },
    { id: 5, label: 'ו\'' },
    { id: 6, label: 'ש\'' }
  ];

  // Load queues
  const loadQueues = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/whatsapp/queues`);
      const data = await response.json();
      setQueues(Array.isArray(data) ? data : (data.queues || []));
    } catch (err) {
      console.error('Error loading queues:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load users from database
  const loadUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/users/all`);
      const data = await response.json();
      setUsers((data.users || []).map(u => ({
        id: u.id || u.username,
        username: u.username,
        name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username,
        email: u.email,
        profile: u.profile,
        isActive: u.isActive !== false,
        avatar: (u.name || u.username || '?')[0]?.toUpperCase()
      })).filter(u => u.isActive));
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, []);

  useEffect(() => {
    loadQueues();
    loadUsers();
  }, [loadQueues, loadUsers]);

  const handleSave = async () => {
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/whatsapp/queues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // 2026-10-18: the loaded updatedAt goes back, so a save over a newer one (another
        // tab, the admin phone app) is refused with 409 instead of overwriting it.
        body: JSON.stringify(editingQueue ? { ...form, id: editingQueue.id, updatedAt: editingQueue.updatedAt } : form)
      });

      if (response.status === 409) {
        alert('הרשומה עודכנה במקום אחר מאז שנפתחה. השמירה נדחתה — רעננו ובצעו את השינוי שוב.');
        return;
      }
      if (response.ok) {
        setShowEditor(false);
        setEditingQueue(null);
        resetForm();
        loadQueues();
      }
    } catch (err) {
      console.error('Error saving queue:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם למחוק את התור?')) return;

    try {
      await fetch(`${API_BASE}/api/whatsapp/queues/${id}`, {
        method: 'DELETE'
      });
      loadQueues();
    } catch (err) {
      console.error('Error deleting queue:', err);
    }
  };

  const handleToggle = async (id) => {
    try {
      await fetch(`${API_BASE}/api/whatsapp/queues/${id}/toggle`, {
        method: 'POST'
      });
      loadQueues();
    } catch (err) {
      console.error('Error toggling queue:', err);
    }
  };

  const handleEdit = (queue) => {
    setEditingQueue(queue);
    setForm({
      name: queue.name,
      description: queue.description || '',
      profiles: queue.profiles || [],
      members: queue.members || [],
      isActive: queue.isActive,
      priority: queue.priority || 1,
      workingHours: queue.workingHours || {
        enabled: false,
        start: '08:30',
        end: '17:00',
        days: [0, 1, 2, 3, 4]
      },
      // 2026-10-15: the server stores `auto_assign` (snake); reading only the camel key
      // showed every queue as ON and re-saved it ON.
      autoAssign: (queue.autoAssign ?? queue.auto_assign) !== false,
      routingType: queue.routingType || 'round_robin'
    });
    // Reset filter UI state so an opened editor starts with the default view.
    // If the saved queue's members include any user whose profile isn't in
    // queue.profiles, the selected-members section will still surface them
    // regardless of the toggle (see Members render block below).
    setMemberSearch('');
    setShowAllUsers(false);
    setShowEditor(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      profiles: [],
      members: [],
      isActive: true,
      priority: 1,
      workingHours: {
        enabled: false,
        start: '08:30',
        end: '17:00',
        days: [0, 1, 2, 3, 4]
      },
      autoAssign: true,
      routingType: 'round_robin'
    });
    setMemberSearch('');
    setShowAllUsers(false);
  };

  const toggleProfile = (profileId) => {
    setForm(prev => ({
      ...prev,
      profiles: prev.profiles.includes(profileId)
        ? prev.profiles.filter(p => p !== profileId)
        : [...prev.profiles, profileId]
    }));
  };

  const toggleMember = (email) => {
    setForm(prev => ({
      ...prev,
      members: prev.members.includes(email)
        ? prev.members.filter(m => m !== email)
        : [...prev.members, email]
    }));
  };

  const toggleDay = (dayId) => {
    setForm(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        days: prev.workingHours.days.includes(dayId)
          ? prev.workingHours.days.filter(d => d !== dayId)
          : [...prev.workingHours.days, dayId].sort()
      }
    }));
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>ניהול תורים</h2>
          <p style={styles.subtitle}>הגדר תורים להפניית הודעות WhatsApp נכנסות</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingQueue(null);
            setShowEditor(true);
          }}
          style={styles.addButton}
        >
          + תור חדש
        </button>
      </div>

      {/* Queues List */}
      {loading ? (
        <div style={styles.loading}>טוען...</div>
      ) : queues.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📋</span>
          <p>אין תורים מוגדרים</p>
          <button onClick={() => setShowEditor(true)} style={styles.emptyButton}>
            צור תור ראשון
          </button>
        </div>
      ) : (
        <div style={styles.queuesList}>
          {queues.map(queue => (
            <div key={queue.id} style={styles.queueCard}>
              <div style={styles.queueHeader}>
                <div style={styles.queueInfo}>
                  <div style={styles.queueName}>
                    <span style={styles.queueIcon}>📋</span>
                    {queue.name}
                    {!queue.isActive && <span style={styles.inactiveBadge}>לא פעיל</span>}
                  </div>
                  {queue.description && (
                    <div style={styles.queueDescription}>{queue.description}</div>
                  )}
                </div>
                <div style={styles.queueActions}>
                  <label style={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={queue.isActive}
                      onChange={() => handleToggle(queue.id)}
                      style={styles.toggleInput}
                    />
                    <span style={{
                      ...styles.toggleSwitch,
                      backgroundColor: queue.isActive ? '#25D366' : '#ccc'
                    }}>
                      <span style={{
                        ...styles.toggleKnob,
                        transform: queue.isActive ? 'translateX(20px)' : 'translateX(0)'
                      }} />
                    </span>
                  </label>
                  <button onClick={() => handleEdit(queue)} style={styles.iconButton}>
                    ✏️
                  </button>
                  <button onClick={() => handleDelete(queue.id)} style={styles.iconButton}>
                    🗑️
                  </button>
                </div>
              </div>

              <div style={styles.queueDetails}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>סוג ניתוב:</span>
                  <span style={styles.detailValue}>
                    {ROUTING_TYPES.find(r => r.id === queue.routingType)?.label || 'סבב'}
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>פרופילים:</span>
                  <span style={styles.detailValue}>
                    {queue.profiles?.length > 0
                      ? queue.profiles.map(p => profileList.find(pr => pr.id === p)?.label || p).join(', ')
                      : 'לא הוגדרו'}
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>חברי צוות:</span>
                  <span style={styles.detailValue}>
                    {queue.members?.length || 0} משתמשים
                  </span>
                </div>
              </div>

              {queue.members?.length > 0 && (
                <div style={styles.membersList}>
                  {queue.members.slice(0, 5).map((email, idx) => {
                    const user = users.find(u => u.email === email);
                    return (
                      <span key={idx} style={styles.memberChip}>
                        {user?.firstName || user?.name || email.split('@')[0]}
                      </span>
                    );
                  })}
                  {queue.members.length > 5 && (
                    <span style={styles.memberMore}>+{queue.members.length - 5}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>{editingQueue ? 'עריכת תור' : 'תור חדש'}</h3>
              <button onClick={() => setShowEditor(false)} style={styles.closeButton}>✕</button>
            </div>

            <div style={styles.modalBody}>
              {/* Basic Info */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>פרטי התור</h4>
                <div style={styles.formGroup}>
                  <label style={styles.label}>שם התור *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    style={styles.input}
                    placeholder="לדוגמה: תור שירות לקוחות"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>תיאור</label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    style={styles.input}
                    placeholder="תיאור קצר של התור"
                  />
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>עדיפות</label>
                    <select
                      value={form.priority}
                      onChange={(e) => setForm(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                      style={styles.select}
                    >
                      <option value={1}>1 - גבוהה</option>
                      <option value={2}>2 - בינונית</option>
                      <option value={3}>3 - נמוכה</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>סוג ניתוב</label>
                    <select
                      value={form.routingType}
                      onChange={(e) => setForm(prev => ({ ...prev, routingType: e.target.value }))}
                      style={styles.select}
                    >
                      {ROUTING_TYPES.map(rt => (
                        <option key={rt.id} value={rt.id}>{rt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Profiles */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>פרופילים</h4>
                <p style={styles.sectionDesc}>בחר את הפרופילים שיכולים לגשת לתור זה</p>
                <div style={styles.profilesGrid}>
                  {profileList.map(profile => (
                    <button
                      key={profile.id}
                      onClick={() => toggleProfile(profile.id)}
                      style={{
                        ...styles.profileButton,
                        ...(form.profiles.includes(profile.id) ? styles.profileButtonActive : {})
                      }}
                    >
                      {profile.label}
                      {form.profiles.includes(profile.id) && ' ✓'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Members */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>חברי צוות</h4>
                <p style={styles.sectionDesc}>
                  בחר את המשתמשים שישויכו לתור זה. ברירת מחדל: מסונן לפי הפרופילים שנבחרו.
                  הפעל "הצג את כל המשתמשים" כדי לבחור משתמש מפרופיל אחר.
                </p>

                {/* Search + show-all toggle */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="חיפוש לפי שם / שם משתמש / אימייל"
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <label style={{ ...styles.checkboxLabel, whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={showAllUsers}
                      onChange={(e) => setShowAllUsers(e.target.checked)}
                    />
                    <span>הצג את כל המשתמשים</span>
                  </label>
                </div>

                {/* Selected-members summary chip row — always visible regardless
                    of the current filter so we can't silently lose anyone. */}
                {form.members.length > 0 && (
                  <div style={{ marginBottom: '10px', fontSize: '13px', color: '#444' }}>
                    <strong>נבחרו {form.members.length}:</strong>{' '}
                    {form.members.map(email => {
                      const u = users.find(x => x.email === email);
                      return (u?.firstName || u?.name || email);
                    }).join(', ')}
                  </div>
                )}

                {(() => {
                  const q = memberSearch.trim().toLowerCase();
                  const matchesQuery = (u) => {
                    if (!q) return true;
                    return (u.name || '').toLowerCase().includes(q)
                        || (u.username || '').toLowerCase().includes(q)
                        || (u.email || '').toLowerCase().includes(q)
                        || (u.firstName || '').toLowerCase().includes(q);
                  };
                  // Selected users are always visible — protects against the
                  // "saved member's profile changed, disappeared from editor"
                  // silent-data-loss bug.
                  const selectedUsers = users.filter(u => form.members.includes(u.email) && matchesQuery(u));
                  const candidatePool = showAllUsers
                    ? users
                    : users.filter(u => form.profiles.includes(u.profile));
                  const otherUsers = candidatePool
                    .filter(u => !form.members.includes(u.email))
                    .filter(matchesQuery);
                  const renderRow = (user) => (
                    <label key={user.email} style={styles.memberItem}>
                      <input
                        type="checkbox"
                        checked={form.members.includes(user.email)}
                        onChange={() => toggleMember(user.email)}
                      />
                      <span style={styles.memberName}>
                        {user.firstName || user.name || user.email}
                      </span>
                      {user.profile && (
                        <span style={styles.memberProfile}>
                          {profileList.find(p => p.id === user.profile)?.label || user.profile}
                        </span>
                      )}
                    </label>
                  );

                  if (selectedUsers.length === 0 && otherUsers.length === 0) {
                    if (!showAllUsers && form.profiles.length === 0) {
                      return (
                        <div style={styles.noProfilesMessage}>
                          <span style={styles.warningIcon}>⚠️</span>
                          <span>בחר פרופיל אחד לפחות או הפעל "הצג את כל המשתמשים"</span>
                        </div>
                      );
                    }
                    return (
                      <div style={styles.noUsersMessage}>
                        {q ? 'לא נמצאו משתמשים תואמים לחיפוש' : 'אין משתמשים בתחום שנבחר'}
                      </div>
                    );
                  }
                  return (
                    <div style={styles.membersGrid}>
                      {selectedUsers.map(renderRow)}
                      {selectedUsers.length > 0 && otherUsers.length > 0 && (
                        <div style={{ borderTop: '1px dashed #ddd', margin: '6px 0' }} />
                      )}
                      {otherUsers.map(renderRow)}
                    </div>
                  );
                })()}
              </div>

              {/* Working Hours */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>שעות פעילות</h4>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.workingHours.enabled}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      workingHours: { ...prev.workingHours, enabled: e.target.checked }
                    }))}
                  />
                  <span>הגבל לשעות פעילות</span>
                </label>

                {form.workingHours.enabled && (
                  <div style={styles.workingHoursConfig}>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>שעת התחלה</label>
                        <input
                          type="time"
                          value={form.workingHours.start}
                          onChange={(e) => setForm(prev => ({
                            ...prev,
                            workingHours: { ...prev.workingHours, start: e.target.value }
                          }))}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>שעת סיום</label>
                        <input
                          type="time"
                          value={form.workingHours.end}
                          onChange={(e) => setForm(prev => ({
                            ...prev,
                            workingHours: { ...prev.workingHours, end: e.target.value }
                          }))}
                          style={styles.input}
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>ימי פעילות</label>
                      <div style={styles.daysGrid}>
                        {DAYS.map(day => (
                          <button
                            key={day.id}
                            onClick={() => toggleDay(day.id)}
                            style={{
                              ...styles.dayButton,
                              ...(form.workingHours.days.includes(day.id) ? styles.dayButtonActive : {})
                            }}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Auto Assign */}
              <div style={styles.section}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.autoAssign}
                    onChange={(e) => setForm(prev => ({ ...prev, autoAssign: e.target.checked }))}
                  />
                  <span>הקצאה אוטומטית לנציג פנוי</span>
                </label>

                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  <span>תור פעיל</span>
                </label>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button onClick={() => setShowEditor(false)} style={styles.cancelBtn}>
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || saving}
                style={styles.saveBtn}
              >
                {saving ? 'שומר...' : 'שמור'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '24px',
    direction: 'rtl',
    maxWidth: '1200px',
    margin: '0 auto'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },

  title: {
    margin: 0,
    fontSize: '20px',
    color: '#181818'
  },

  subtitle: {
    margin: '8px 0 0 0',
    color: '#706e6b',
    fontSize: '14px'
  },

  addButton: {
    padding: '10px 20px',
    backgroundColor: '#25D366',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#706e6b'
  },

  empty: {
    textAlign: 'center',
    padding: '60px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },

  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px'
  },

  emptyButton: {
    marginTop: '16px',
    padding: '10px 24px',
    backgroundColor: '#0176d3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  queuesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },

  queueCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },

  queueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },

  queueInfo: {},

  queueName: {
    fontSize: '16px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  queueIcon: {
    fontSize: '20px'
  },

  inactiveBadge: {
    fontSize: '11px',
    padding: '2px 8px',
    backgroundColor: '#f5f5f5',
    color: '#706e6b',
    borderRadius: '4px'
  },

  queueDescription: {
    marginTop: '4px',
    color: '#706e6b',
    fontSize: '13px'
  },

  queueActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },

  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },

  toggleInput: {
    display: 'none'
  },

  toggleSwitch: {
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    position: 'relative',
    transition: 'background-color 0.2s'
  },

  toggleKnob: {
    width: '20px',
    height: '20px',
    backgroundColor: 'white',
    borderRadius: '50%',
    position: 'absolute',
    top: '2px',
    right: '2px',
    transition: 'transform 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
  },

  iconButton: {
    padding: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px'
  },

  queueDetails: {
    marginTop: '16px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px'
  },

  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  detailLabel: {
    fontSize: '12px',
    color: '#706e6b'
  },

  detailValue: {
    fontSize: '13px',
    color: '#181818'
  },

  membersList: {
    marginTop: '12px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },

  memberChip: {
    padding: '4px 10px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '12px',
    fontSize: '12px'
  },

  memberMore: {
    padding: '4px 10px',
    backgroundColor: '#f5f5f5',
    color: '#706e6b',
    borderRadius: '12px',
    fontSize: '12px'
  },

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },

  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '700px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e5e5e5'
  },

  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#706e6b'
  },

  modalBody: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },

  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #e5e5e5'
  },

  section: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f0f0f0'
  },

  sectionTitle: {
    margin: '0 0 8px 0',
    fontSize: '15px',
    fontWeight: '600',
    color: '#181818'
  },

  sectionDesc: {
    margin: '0 0 12px 0',
    fontSize: '13px',
    color: '#706e6b'
  },

  formRow: {
    display: 'flex',
    gap: '16px'
  },

  formGroup: {
    flex: 1,
    marginBottom: '16px'
  },

  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '13px',
    fontWeight: '500'
  },

  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d8dde6',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },

  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d8dde6',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },

  profilesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },

  profileButton: {
    padding: '8px 16px',
    border: '1px solid #d8dde6',
    borderRadius: '20px',
    backgroundColor: 'white',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },

  profileButtonActive: {
    backgroundColor: '#e8f5e9',
    borderColor: '#25D366',
    color: '#2e7d32'
  },

  membersGrid: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    padding: '8px'
  },

  noProfilesMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '8px',
    color: '#f57c00',
    fontSize: '14px'
  },

  warningIcon: {
    fontSize: '18px'
  },

  noUsersMessage: {
    padding: '16px',
    textAlign: 'center',
    color: '#706e6b',
    fontSize: '14px'
  },

  memberItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '4px'
  },

  memberName: {
    flex: 1,
    fontSize: '14px'
  },

  memberProfile: {
    fontSize: '11px',
    color: '#706e6b',
    padding: '2px 6px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  },

  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '12px'
  },

  workingHoursConfig: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },

  daysGrid: {
    display: 'flex',
    gap: '8px'
  },

  dayButton: {
    width: '40px',
    height: '40px',
    border: '1px solid #d8dde6',
    borderRadius: '8px',
    backgroundColor: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },

  dayButtonActive: {
    backgroundColor: '#25D366',
    borderColor: '#25D366',
    color: 'white'
  },

  cancelBtn: {
    padding: '10px 24px',
    backgroundColor: '#f5f5f5',
    color: '#181818',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  saveBtn: {
    padding: '10px 24px',
    backgroundColor: '#0176d3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default WhatsAppQueueManager;
